<template>
  <div>
    <h2>Terms of Service</h2>
    <p>
      These Terms of Service govern your use of the website located at
      <a href="https://www.cofocus.one">https://www.cofocus.one</a> and any related services provided by ElysiumOne OÜ.
    </p>
    <p>
      By accessing
      <a href="https://www.cofocus.one">https://www.cofocus.one</a>, you agree to abide by these Terms of Service and to comply with all applicable
      laws and regulations. If you do not agree with these Terms of Service, you are prohibited from using or accessing this website or using any
      other services provided by ElysiumOne OÜ.
    </p>
    <p>
      We, ElysiumOne OÜ, reserve the right to review and amend any of these Terms of Service at our sole discretion. Upon doing so, we will update
      this page. Any changes to these Terms of Service will take effect immediately from the date of publication.
    </p>
    <p>These Terms of Service were last updated on November 10, 2021.</p>
    <h3>Limitations of Use</h3>
    <p>By using this website, you warrant on behalf of yourself, your users, and other parties you represent that you will not:</p>
    <ol>
      <li>modify, copy, prepare derivative works of, decompile, or reverse engineer any materials and software contained on this website;</li>
      <li>remove any copyright or other proprietary notations from any materials and software on this website;</li>
      <li>transfer the materials to another person or “mirror” the materials on any other server;</li>
      <li>
        knowingly or negligently use this website or any of its associated services in a way that abuses or disrupts our networks or any other service
        ElysiumOne OÜ provides;
      </li>
      <li>use this website or its associated services to transmit or publish any harassing, indecent, obscene, fraudulent, or unlawful material;</li>
      <li>use this website or its associated services in violation of any applicable laws or regulations;</li>
      <li>use this website in conjunction with sending unauthorized advertising or spam;</li>
      <li>harvest, collect, or gather user data without the user’s consent; or</li>
      <li>
        use this website or its associated services in such a way that may infringe the privacy, intellectual property rights, or other rights of
        third parties.
      </li>
    </ol>
    <h3>Intellectual Property</h3>
    <p>
      The intellectual property in the materials contained in this website are owned by or licensed to ElysiumOne OÜ and are protected by applicable
      copyright and trademark law. We grant our users permission to download one copy of the materials for personal, non-commercial transitory use.
    </p>
    <p>
      This constitutes the grant of a license, not a transfer of title. This license shall automatically terminate if you violate any of these
      restrictions or the Terms of Service, and may be terminated by ElysiumOne OÜ at any time.
    </p>
    <h3>Age requirements</h3>
    <p>
      Creating or owning a Cofocus account, or using a Cofocus account has the age requirement of 17 years old. However, if law requires that you must
      be older in order for Cofocus to lawfully provide service to you without parental consent (including using of your personal data), then the age
      requirement to use Cofocus is such older age required by local law.
    </p>
    <h3>Paid Services</h3>
    <p>
      (a) Price. Cofocus may at any time require users to pay a fee to use the Services or any part thereof, including to access services or features
      that were previously offered to users free of charge. Prices displayed for the Services exclude all taxes or related costs which will be added
      to the amount of your purchase and itemized on the check-out page. You will have an opportunity to review taxes and costs before you confirm
      your purchase. All prices shown on the Services are subject to change at any time without notice.
    </p>
    <p>
      (b) Payment. If you wish to purchase Services, you may be asked to supply certain relevant information, such as your payment card number and its
      expiration date, your billing address and your shipping information to our payment processors. You represent and warrant that you have the right
      to use any payment card that you submit in connection with a transaction. By submitting such information, you grant to us the right to provide
      such information to third parties for purposes of facilitating such transactions. Verification of information may be required prior to the
      acknowledgment or completion of any transaction. You agree to pay all charges incurred by you or on your behalf through the Services, at the
      prices in effect when such charges are incurred, including all taxes applicable to your purchases. In the event legal action is necessary to
      collect on balances due, you will reimburse us and our vendors or agents for all expenses incurred to recover sums due, including attorneys’
      fees and other legal expenses.
    </p>
    <p>
      (c) Subscriptions. Your purchase of certain Services may require enrollment in a payment plan involving automatic renewal (a “Recurring
      Subscription”), and if you purchase such a Recurring Subscription, you authorize Cofocus to maintain your account information and charge that
      account automatically upon the renewal with no further action required by you. The length of your Recurring Subscription will be provided when
      you make your purchase. Your Recurring Subscription will automatically renew unless you cancel it. In the event that Cofocus is unable to charge
      your account as authorized by you when you enrolled in a Recurring Subscription, Cofocus, may, in its sole discretion: (i) bill you for your
      Services and suspend your access to the Services until payment is received, and/or (ii) seek to update your account information through third
      party sources (i.e., your bank or a payment processor) to continue charging your account as authorized by you. You may cancel your subscription
      through your account by visiting <a href="https://www.cofocus.one/?settings=billing">https://www.cofocus.one/?settings=billing</a>. You may
      cancel a Recurring Subscription at any time, but if you cancel your Recurring Subscription before the end of the current subscription period, we
      will not refund any subscription fees already paid to us. Following any cancellation, however, you will continue to have access to the
      applicable Services through the end of your current subscription period. Cofocus may change the prices charged for Recurring Subscriptions at
      any time by posting updated pricing through the Services; provided, however, that the prices for your Recurring Subscription will remain in
      force for the duration of the subscription period for which you have paid. After that period ends, your use of the applicable Services will be
      charged at the then-current subscription price. If you do not agree to these price changes, you must cancel your Recurring Subscription at least
      ten days before the changes take effect. If you do not cancel, your Recurring Subscription will automatically renew at the then-current price at
      the time of renewal and for the same duration as the initial subscription term, and Cofocus will charge your on-file payment card or method on
      the first day of the renewal of the subscription term.
    </p>
    <p>
      (d) Order Confirmation; Acceptance. You will have the opportunity to review and confirm your order, including payment method and other details
      of your order. Although we may confirm orders by e-mail, the receipt of an e-mail order confirmation does not constitute our acceptance of an
      order or our confirmation of an offer to sell a product or service. We will display or send a notice when we ultimately accept your order, and
      our acceptance will be complete at the time we display or send the formal acceptance notice. Payment must be received by us before our
      acceptance of an order.
    </p>
    <p>
      (e) Lifetime Plan. Cofocus offers a lifetime plan for certain Services, which entails a one-time payment in exchange for perpetual access to
      designated features or services. By purchasing the lifetime plan, you acknowledge and agree that:
    </p>
    <p>
      (i) The one-time payment grants you lifetime access to the specified Services, subject to the terms and conditions outlined herein. (ii) Your
      purchase of the lifetime plan is non-refundable, except as required by applicable law. (iii) Cofocus reserves the right to modify, suspend, or
      discontinue any aspect of the lifetime plan or associated Services at any time, with or without notice, without liability to you. (iv) Your use
      of the Services under the lifetime plan is subject to compliance with all terms and conditions herein and any additional terms specific to the
      lifetime plan. (v) Cofocus retains the right to terminate your access to the lifetime plan and associated Services for any violation of these
      terms or misuse of the Services, without refund. (vi) The lifetime plan is non-transferable and may not be assigned or sublicensed without prior
      written consent from Cofocus. (vii) Any disputes regarding the lifetime plan or associated Services shall be resolved in accordance with the
      provisions outlined herein.
    </p>
    <p>
      By purchasing the lifetime plan, you signify your acceptance of these terms and conditions governing the lifetime plan in addition to the
      general terms and conditions set forth in this Agreement.
    </p>
    <h3>User-Generated Content</h3>
    <p>
      You retain your intellectual property ownership rights over content you submit to us for publication on our website. We will never claim
      ownership of your content, but we do require a license from you in order to use it.
    </p>
    <p>
      When you use our website or its associated services to post, upload, share, or otherwise transmit content covered by intellectual property
      rights, you grant to us a non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to use, distribute, modify, run, copy,
      publicly display, translate, or otherwise create derivative works of your content in a manner that is consistent with your privacy preferences
      and our Privacy Policy.
    </p>
    <p>
      The license you grant us can be terminated at any time by deleting your content or account. However, to the extent that we (or our partners)
      have used your content in connection with commercial or sponsored content, the license will continue until the relevant commercial or post has
      been discontinued by us.
    </p>
    <p>
      You give us permission to use your username and other identifying information associated with your account in a manner that is consistent with
      your privacy preferences, and our Privacy Policy.
    </p>
    <h3>Liability</h3>
    <p>
      Our website and the materials on our website are provided on an 'as is' basis. To the extent permitted by law, ElysiumOne OÜ makes no
      warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or
      conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property, or other violation of rights.
    </p>
    <p>
      In no event shall ElysiumOne OÜ or its suppliers be liable for any consequential loss suffered or incurred by you or any third party arising
      from the use or inability to use this website or the materials on this website, even if ElysiumOne OÜ or an authorized representative has been
      notified, orally or in writing, of the possibility of such damage.
    </p>
    <p>
      In the context of this agreement, &ldquo;consequential loss&rdquo; includes any consequential loss, indirect loss, real or anticipated loss of
      profit, loss of benefit, loss of revenue, loss of business, loss of goodwill, loss of opportunity, loss of savings, loss of reputation, loss of
      use and/or loss or corruption of data, whether under statute, contract, equity, tort (including negligence), indemnity, or otherwise.
    </p>
    <p>
      Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages,
      these limitations may not apply to you.
    </p>
    <h3>Accuracy of Materials</h3>
    <p>
      The materials appearing on our website are not comprehensive and are for general information purposes only. ElysiumOne OÜ does not warrant or
      make any representations concerning the accuracy, likely results, or reliability of the use of the materials on this website, or otherwise
      relating to such materials or on any resources linked to this website.
    </p>
    <h3>Links</h3>
    <p>
      ElysiumOne OÜ has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site. The
      inclusion of any link does not imply endorsement, approval, or control by ElysiumOne OÜ of the site. Use of any such linked site is at your own
      risk and we strongly advise you make your own investigations with respect to the suitability of those sites.
    </p>
    <h3>Right to Terminate</h3>
    <p>
      We may suspend or terminate your right to use our website and terminate these Terms of Service immediately upon written notice to you for any
      breach of these Terms of Service.
    </p>
    <h3>Severance</h3>
    <p>
      Any term of these Terms of Service which is wholly or partially void or unenforceable is severed to the extent that it is void or unenforceable.
      The validity of the remainder of these Terms of Service is not affected.
    </p>
    <h3>Governing Law</h3>
    <p>
      These Terms of Service are governed by and construed in accordance with the laws of Estonia. You irrevocably submit to the exclusive
      jurisdiction of the courts in that State or location.
    </p>
  </div>
</template>

<script>
export default {
  name: "Terms",
  mounted() {
    window.mixpanel.track("Visit Terms Of Service");
  },
};
</script>
